import {
  RenderTreeNodePayload,
  Tree,
  TreeNodeData,
  useTree,
  UseTreeReturnType,
} from "@mantine/core"
import { getSelectedNodes } from "./getSelectedNodes"
import { defaultNodeRenderer } from "./default-node-renderer"
import { TreeNode } from "../../@client/Theasoft_ApiServer__Utils_TreeBase"
import { useRef } from "react"

// this is necessary because TreeNodeData doesnt have a Type Parameter for TreeNodeData.nodeProps
// and their nodePropsType: Record<string, any> makes autocompletion impossible
interface TypedTreeNodeData<NodeProps> extends Omit<TreeNodeData, "nodeProps"> {
  nodeProps: NodeProps
}

// this is necessary because RenderTreeNodePayload also doesnt have a generic type parameter for the node props
// because mantine devs (allegedly) didnt think this through and just really liked their way of doing things, so
// they didnt bother to make these interfaces do more than what they needed in their project, like most opinionated UI-Libraries
export interface TypedRenderTreeNodePayload<NodeProps>
  extends Omit<RenderTreeNodePayload, "node"> {
  node: TypedTreeNodeData<NodeProps>
}

export interface TreeRendererProps<NodeProps, Context> {
  tree: TreeNode<NodeProps>[]
  context?: Context
  nodeRenderer?: (
    node: TypedRenderTreeNodePayload<NodeProps>,
    context: Context extends undefined ? undefined : Exclude<Context, undefined>
  ) => React.ReactNode
  renderContext?: Context
  onCheckedStateChange?: (checkedNodeValues: string[]) => void
  treeState?: UseTreeReturnType
  initialCheckedState?: string[]
  initialExpandedState?: Record<string, boolean>
}

function arraysAreEqual<T>(arr1: T[], arr2: T[]) {
  if (arr1.length !== arr2.length) return false
  return arr1.every((value, index) => value === arr2[index])
}

export function TreeRenderer<
  NodeProps extends Record<string, any>,
  NodeRenderContext,
>({
  tree,
  initialCheckedState,
  initialExpandedState,
  context,
  nodeRenderer,
  onCheckedStateChange,
  treeState,
}: TreeRendererProps<NodeProps, NodeRenderContext>) {
  const previousDataRef = useRef<undefined | string[]>()

  const treeStateData = useTree({
    initialCheckedState: initialCheckedState ?? undefined,
    initialExpandedState: initialExpandedState ?? undefined,
  })

  const internalTreeState = treeState ?? treeStateData

  if (
    !arraysAreEqual(
      previousDataRef.current || [],
      internalTreeState.checkedState
    )
  ) {
    onCheckedStateChange &&
      onCheckedStateChange(
        getSelectedNodes(tree, internalTreeState.getCheckedNodes()).map(
          (v) => v.value
        )
      )
    previousDataRef.current = internalTreeState.checkedState
  }

  return (
    <Tree
      data={tree}
      expandOnClick={false}
      renderNode={
        nodeRenderer
          ? (node) => {
              return nodeRenderer(
                node as TypedRenderTreeNodePayload<NodeProps>,
                context as NodeRenderContext extends undefined
                  ? undefined
                  : Exclude<NodeRenderContext, undefined>
              )
            }
          : defaultNodeRenderer
      }
      tree={internalTreeState}
    />
  )
}
